"use client"

import { useId, useState } from "react"
import { Eyebrow, GlassCard, Reveal } from "@/components/glass/glass-kit"

export type PricingFaqItem = { q: string; a: string }

function FaqRow({
  faq,
  open,
  onToggle,
  first,
}: {
  faq: PricingFaqItem
  open: boolean
  onToggle: () => void
  first: boolean
}) {
  const panelId = useId()

  return (
    <div style={{ borderTop: first ? undefined : "1px solid var(--pg-line)" }}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className="flex w-full items-start gap-3 px-5 py-5 text-left sm:px-6"
      >
        <span className="min-w-0 flex-1 text-[15px] font-medium leading-snug tracking-tight" style={{ color: "var(--pg-fg)" }}>
          {faq.q}
        </span>
        <span
          className="mt-0.5 shrink-0 text-lg font-light leading-none transition-transform duration-300 ease-out"
          style={{ color: open ? "var(--pg-fg)" : "var(--pg-faint)", transform: open ? "rotate(90deg)" : undefined }}
          aria-hidden
        >
          ›
        </span>
      </button>
      <div
        id={panelId}
        className="grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <p className="px-5 pb-5 text-[14px] leading-relaxed sm:px-6 sm:pb-6" style={{ color: "var(--pg-muted)" }}>
            {faq.a}
          </p>
        </div>
      </div>
    </div>
  )
}

export function PricingFaq({
  faqs,
  eyebrow,
  title,
  subtitle,
}: {
  faqs: PricingFaqItem[]
  eyebrow: string
  title: string
  subtitle: string
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section className="mt-20 pt-16 sm:mt-24 sm:pt-20" style={{ borderTop: "1px solid var(--pg-line)" }}>
      <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.25fr)] lg:gap-14 xl:gap-20">
        <Reveal>
          <div className="lg:sticky lg:top-28">
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2 className="mt-5 text-balance text-[clamp(1.85rem,4vw,2.6rem)] font-semibold leading-[1.08] tracking-[-0.035em]">
              {title}
            </h2>
            <p className="mt-4 max-w-md text-[14.5px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
              {subtitle}
            </p>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <GlassCard>
            {faqs.map((faq, i) => (
              <FaqRow
                key={faq.q}
                faq={faq}
                first={i === 0}
                open={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              />
            ))}
          </GlassCard>
        </Reveal>
      </div>
    </section>
  )
}
